import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ClipboardList, Download, Search } from 'lucide-react'
import { Badge, Bone, Input, SegmentedControl, Select } from '@/components/ui'
import { Avatar } from '@/components/common/Avatar'
import { TablePaginationBar } from '@/components/common/TablePaginationBar'
import { usePermissions } from '@/hooks/usePermissions'
import { cn } from '@/lib/cn'
import type {
  OrganizationDashboard,
  OrganizationDashboardRosterRow,
  OrganizationDashboardRosterStatus,
} from '@/types/dashboard.types'
import { DashboardDetailModal, DashboardTile } from './DashboardTile'
import {
  ROSTER_STATUS_META,
  downloadCsv,
  formatClock,
  formatMinutes,
  formatNumber,
} from './dashboardFormat'

const CARD_ROWS = 7
const PAGE_SIZES = [25, 50, 100] as const

function rowKey(row: OrganizationDashboardRosterRow): string {
  return `${row.companyId}:${row.userId}`
}

function exportRoster(rows: OrganizationDashboardRosterRow[], timezone?: string): void {
  downloadCsv({
    filename: 'attendance',
    header: [
      'Employee',
      'Employee ID',
      'Company',
      'Status',
      'Check-in',
      'Check-out',
      'Worked (min)',
      'Late (min)',
    ],
    rows: rows.map((row) => [
      row.name,
      row.employeeId ?? '',
      row.companyName,
      ROSTER_STATUS_META[row.status].label,
      row.clockIn ? formatClock(row.clockIn, timezone) : '',
      row.clockOut ? formatClock(row.clockOut, timezone) : '',
      row.workedMinutes,
      row.lateByMinutes,
    ]),
    noun: ['row', 'rows'],
  })
}

function StatusCell({ row }: { row: OrganizationDashboardRosterRow }): React.ReactElement {
  const meta = ROSTER_STATUS_META[row.status]
  return (
    <Badge variant={meta.variant} size="sm" dot={row.isClockedIn}>
      {meta.label}
      {row.status === 'late' && row.lateByMinutes > 0
        ? ` · ${formatMinutes(row.lateByMinutes)}`
        : ''}
    </Badge>
  )
}

function RosterTable({
  rows,
  timezone,
  canOpen,
  dense,
}: {
  rows: OrganizationDashboardRosterRow[]
  timezone?: string
  canOpen: boolean
  dense?: boolean
}): React.ReactElement {
  const cell = dense ? 'px-5 py-2' : 'px-4 py-2.5'
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-[13px] leading-5">
        <thead className="border-y border-gray-100 bg-gray-50/70 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:border-gray-700/70 dark:bg-gray-900/30 dark:text-gray-400">
          <tr>
            <th scope="col" className={cn(cell, 'text-left')}>
              Employee
            </th>
            <th scope="col" className={cn(cell, 'hidden text-left md:table-cell')}>
              Company
            </th>
            <th scope="col" className={cn(cell, 'text-left')}>
              Status
            </th>
            <th scope="col" className={cn(cell, 'text-right')}>
              Check-in
            </th>
            <th scope="col" className={cn(cell, 'hidden text-right sm:table-cell')}>
              Check-out
            </th>
            <th scope="col" className={cn(cell, 'text-right')}>
              Worked
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 tabular-nums dark:divide-gray-700/70">
          {rows.map((row) => (
            <tr
              key={rowKey(row)}
              className="transition-colors hover:bg-gray-50/80 dark:hover:bg-gray-700/30"
            >
              <td className={cell}>
                <div className="flex min-w-0 items-center gap-2.5">
                  <Avatar name={row.name} size="sm" tone="soft" />
                  <div className="min-w-0">
                    {canOpen ? (
                      <Link
                        to={`/companies/${row.companyId}/attendance`}
                        className="block truncate font-medium text-gray-900 hover:text-primary hover:underline dark:text-gray-100 dark:hover:text-sky-300"
                      >
                        {row.name}
                      </Link>
                    ) : (
                      <span className="block truncate font-medium text-gray-900 dark:text-gray-100">
                        {row.name}
                      </span>
                    )}
                    <span className="block truncate text-xs text-gray-400 dark:text-gray-500 md:hidden">
                      {row.companyName}
                    </span>
                    {row.employeeId ? (
                      <span className="hidden truncate text-xs text-gray-400 dark:text-gray-500 md:block">
                        {row.employeeId}
                      </span>
                    ) : null}
                  </div>
                </div>
              </td>
              <td
                className={cn(
                  cell,
                  'hidden max-w-[12rem] truncate text-gray-600 dark:text-gray-300 md:table-cell'
                )}
              >
                {row.companyName}
              </td>
              <td className={cell}>
                <StatusCell row={row} />
              </td>
              <td className={cn(cell, 'text-right text-gray-700 dark:text-gray-200')}>
                {formatClock(row.clockIn, timezone)}
              </td>
              <td
                className={cn(
                  cell,
                  'hidden text-right text-gray-700 dark:text-gray-200 sm:table-cell'
                )}
              >
                {row.isClockedIn ? (
                  <span className="text-emerald-700 dark:text-emerald-400">Working</span>
                ) : (
                  formatClock(row.clockOut, timezone)
                )}
              </td>
              <td className={cn(cell, 'text-right font-medium text-gray-900 dark:text-gray-100')}>
                {row.workedMinutes > 0 ? (
                  formatMinutes(row.workedMinutes)
                ) : (
                  <span className="text-gray-300 dark:text-gray-600">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** Today's roster with each person's standing and hours — the operational table from the brief. */
export function EmployeeAttendanceCard({
  data,
  isLoading,
  onExpand,
  className,
}: {
  data: OrganizationDashboard | undefined
  isLoading: boolean
  onExpand: () => void
  className?: string
}): React.ReactElement {
  const { can } = usePermissions()
  const roster = data?.todayRoster ?? []
  const scheduled = roster.filter((row) => row.status !== 'not-scheduled')
  const visible = (scheduled.length > 0 ? scheduled : roster).slice(0, CARD_ROWS)
  const total = data?.todayRosterTotal ?? roster.length

  return (
    <DashboardTile
      title="Employee attendance"
      subtitle={
        data?.todayRoster
          ? `${formatNumber(scheduled.length)} scheduled or recorded ${data.isLive === false ? 'that day' : 'today'} · ${formatNumber(total)} on the roster`
          : 'Today'
      }
      icon={ClipboardList}
      onExpand={onExpand}
      className={className}
      actions={
        roster.length > 0 ? (
          <button
            type="button"
            onClick={() => exportRoster(roster, data?.timezone)}
            className="inline-flex h-7 items-center gap-1.5 rounded-md border border-gray-200 px-2 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700/50"
          >
            <Download className="h-3.5 w-3.5" aria-hidden />
            Export
          </button>
        ) : null
      }
      footer={
        roster.length > visible.length ? (
          <button
            type="button"
            onClick={onExpand}
            className="text-xs font-medium text-primary hover:underline dark:text-sky-300"
          >
            Show all {formatNumber(total)} people
          </button>
        ) : undefined
      }
    >
      {isLoading ? (
        <div className="space-y-3 px-5 pb-4">
          {Array.from({ length: 5 }, (_, i) => (
            <Bone key={i} className="h-8 w-full rounded-md" />
          ))}
        </div>
      ) : visible.length === 0 ? (
        <div className="grid min-h-40 place-items-center px-6 pb-4 text-center text-sm text-gray-500 dark:text-gray-400">
          {data?.todayRoster
            ? 'Nobody is on an active roster yet.'
            : "Today's per-person table will show here once the server is updated."}
        </div>
      ) : (
        <RosterTable
          rows={visible}
          timezone={data?.timezone}
          canOpen={can('attendance', 'read')}
          dense
        />
      )}
    </DashboardTile>
  )
}

type Scope = 'scheduled' | 'everyone'

const SCOPE_OPTIONS = [
  { value: 'scheduled' as const, label: 'Scheduled' },
  { value: 'everyone' as const, label: 'Everyone' },
]

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'working', label: 'Clocked in now' },
  ...(Object.keys(ROSTER_STATUS_META) as OrganizationDashboardRosterStatus[]).map((status) => ({
    value: status,
    label: ROSTER_STATUS_META[status].label,
  })),
]

export function EmployeeAttendanceModal({
  open,
  data,
  onClose,
}: {
  open: boolean
  data: OrganizationDashboard | undefined
  onClose: () => void
}): React.ReactElement | null {
  const { can } = usePermissions()
  const [search, setSearch] = useState('')
  const [scope, setScope] = useState<Scope>('scheduled')
  const [status, setStatus] = useState('all')
  const [companyId, setCompanyId] = useState('all')
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState<number>(PAGE_SIZES[0])
  const roster = useMemo(() => data?.todayRoster ?? [], [data?.todayRoster])

  const companyOptions = useMemo(() => {
    const names = new Map(roster.map((row) => [row.companyId, row.companyName]))
    return [
      { value: 'all', label: 'All companies' },
      ...Array.from(names, ([value, label]) => ({ value, label })).sort((a, b) =>
        a.label.localeCompare(b.label)
      ),
    ]
  }, [roster])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return roster.filter((row) => {
      if (scope === 'scheduled' && row.status === 'not-scheduled') return false
      if (status === 'working' && !row.isClockedIn) return false
      if (status !== 'all' && status !== 'working' && row.status !== status) return false
      if (companyId !== 'all' && row.companyId !== companyId) return false
      if (q === '') return true
      return (
        row.name.toLowerCase().includes(q) || (row.employeeId?.toLowerCase().includes(q) ?? false)
      )
    })
  }, [roster, scope, status, companyId, search])

  // Any filter change goes back to page 1 (adjusted during render, not in an effect).
  const filterKey = JSON.stringify([search, scope, status, companyId, limit])
  const [pageFilterKey, setPageFilterKey] = useState(filterKey)
  if (pageFilterKey !== filterKey) {
    setPageFilterKey(filterKey)
    setPage(1)
  }

  if (!open) return null

  const totalPages = Math.max(1, Math.ceil(filtered.length / limit))
  const currentPage = Math.min(page, totalPages)
  const pageRows = filtered.slice((currentPage - 1) * limit, currentPage * limit)
  const truncated = (data?.todayRosterTotal ?? 0) > roster.length

  return (
    <DashboardDetailModal
      open
      onClose={onClose}
      title={
        data?.isLive === false && data.asOf
          ? `Employee attendance · ${data.asOf}`
          : 'Employee attendance today'
      }
      subtitle={
        truncated
          ? `Showing the first ${formatNumber(roster.length)} of ${formatNumber(data?.todayRosterTotal)} people, most urgent first. The attendance page has everyone.`
          : 'Everyone on an active roster, most urgent first: clocked in, late, absent, then the rest.'
      }
      toolbar={
        <button
          type="button"
          onClick={() => exportRoster(filtered, data?.timezone)}
          disabled={filtered.length === 0}
          className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-gray-200 px-3 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700/50"
        >
          <Download className="h-3.5 w-3.5" aria-hidden />
          Export {formatNumber(filtered.length)}
        </button>
      }
    >
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <SegmentedControl
          options={SCOPE_OPTIONS}
          value={scope}
          onChange={setScope}
          ariaLabel="Who to show"
          size="sm"
        />
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400"
            aria-hidden
          />
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name or ID…"
            aria-label="Search employees"
            className="h-9 w-52 pl-8"
          />
        </div>
        <div className="w-44">
          <Select
            options={STATUS_OPTIONS}
            value={status}
            onChange={(v) => setStatus(v || 'all')}
            clearable={false}
          />
        </div>
        <div className="w-48">
          <Select
            options={companyOptions}
            value={companyId}
            onChange={(v) => setCompanyId(v || 'all')}
            clearable={false}
          />
        </div>
      </div>
      {pageRows.length === 0 ? (
        <p className="py-16 text-center text-sm text-gray-500 dark:text-gray-400">
          {roster.length === 0
            ? 'Nobody is on an active roster yet.'
            : 'Nobody matches these filters.'}
        </p>
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700 [&_thead]:border-t-0">
          <RosterTable
            rows={pageRows}
            timezone={data?.timezone}
            canOpen={can('attendance', 'read')}
          />
        </div>
      )}
      {filtered.length > PAGE_SIZES[0] ? (
        <div className="mt-4">
          <TablePaginationBar
            page={currentPage}
            limit={limit}
            total={filtered.length}
            totalPages={totalPages}
            onPageChange={setPage}
            pageSizeOptions={PAGE_SIZES}
            onLimitChange={setLimit}
          />
        </div>
      ) : null}
    </DashboardDetailModal>
  )
}
